import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFamilyRole, type FamilyRole } from '@/hooks/useFamilyRole';
import { useUserRole } from '@/hooks/useUserRole';
import {
  resolveFamilyCapabilities,
  type FamilyCapabilityFlags,
  type FamilyPermissionKey as SharedFamilyPermissionKey,
} from '@/lib/familyCapabilities';

// Mirrors the enum in migration 20260507152704
export type FamilyPermissionKey = SharedFamilyPermissionKey;

export interface FamilyCapabilities extends FamilyCapabilityFlags {
  loading: boolean;
  hasFamilyRole: boolean;
  role: FamilyRole | null;
}

/**
 * Resolves the current user's capabilities inside the family group they
 * belong to. Explicit per-user grants in `family_member_permissions` override
 * the role preset. Owners and admins always get every capability.
 *
 * When the user isn't in any family group at all (personal-only account),
 * every capability defaults to `true` so the personal app is unaffected.
 */
export function useFamilyCapabilities(): FamilyCapabilities {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const familyRole = useFamilyRole();

  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['family-permission-overrides', user?.id, familyRole.groupId],
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!user?.id || !familyRole.groupId) return {};
      const { data, error } = await supabase
        .from('family_member_permissions')
        .select('permission_key, granted')
        .eq('group_id', familyRole.groupId)
        .eq('user_id', user.id);
      if (error) return {};
      const map: Record<string, boolean> = {};
      for (const row of data || []) map[row.permission_key] = row.granted;
      return map;
    },
    enabled: !!user?.id && !!familyRole.groupId && familyRole.role !== 'owner',
    staleTime: 60_000,
  });

  const loading = familyRole.loading || overridesLoading;

  const flags = resolveFamilyCapabilities({
    isMember: familyRole.isMember,
    role: familyRole.role,
    isAdmin,
    overrides,
  });

  return {
    loading,
    hasFamilyRole: familyRole.isMember && !!familyRole.role,
    role: familyRole.role,
    ...flags,
  };
}
